import React from 'react';
import Background from './Background';
import Logo from './Logo';
import Countdown from './Countdown';
import NotifyForm from './NotifyForm';
import AppBadges from './AppBadges';

// Launch target — 60 days out from first render.
const LAUNCH_TARGET = Date.now() + 1000 * 60 * 60 * 24 * 60;

const highlights = [
  { label: '100% Genuine OEM', icon: 'shield' },
  { label: 'Global Sourcing', icon: 'globe' },
  { label: 'Verified Quality', icon: 'check' },
];

function Icon({ name }) {
  const paths = {
    shield: 'M12 3l7 3v6c0 4-3 6.5-7 9-4-2.5-7-5-7-9V6l7-3Z',
    globe: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 0c3 3 3 15 0 18M3 12h18',
    check: 'M20 7L10 17l-5-5',
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d={paths[name]}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ComingSoon() {
  return (
    <main className="relative min-h-screen bg-ink-900">
      <Background />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col px-5 py-6 sm:px-10 sm:py-8">
        {/* Header */}
        <header className="flex animate-fade-up items-center justify-between">
          <Logo />
          <span className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs font-medium text-white/60 sm:inline-flex">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ember-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-ember-500" />
            </span>
            Launching Soon
          </span>
        </header>

        {/* Hero */}
        <section className="flex flex-1 flex-col items-center justify-center py-10 text-center sm:py-12">
          <span className="mb-5 inline-flex animate-fade-up items-center gap-2 rounded-full border border-ember-500/30 bg-ember-500/10 px-4 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-ember-300 sm:mb-6 sm:text-xs sm:tracking-[0.25em] [animation-delay:0.1s]">
            <span aria-hidden="true">🔥</span>
            The Future of Auto Parts
          </span>

          <h1 className="animate-fade-up font-display text-[2.6rem] font-extrabold leading-[0.95] tracking-tight sm:text-7xl md:text-8xl [animation-delay:0.15s]">
            <span className="block text-white">Something</span>
            <span className="block text-gradient-fire text-fire-glow animate-flame-flicker">
              On Fire
            </span>
            <span className="mt-2 block text-xl font-semibold text-white/70 sm:text-3xl">
              is coming soon.
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-xl animate-fade-up text-[0.95rem] leading-relaxed text-white/55 sm:mt-7 sm:text-lg [animation-delay:0.25s]">
            Carpetta is building the premium marketplace for{' '}
            <span className="text-white/80">genuine OEM used car parts</span> —
            sourced, verified, and delivered with precision. Quality you can
            trust, performance you can feel.
          </p>

          {/* Highlights */}
          <ul className="mt-7 flex animate-fade-up flex-wrap items-center justify-center gap-2 sm:mt-8 sm:gap-3 [animation-delay:0.3s]">
            {highlights.map((h) => (
              <li
                key={h.label}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/70 sm:px-4 sm:py-2 sm:text-sm"
              >
                <span className="text-ember-400">
                  <Icon name={h.icon} />
                </span>
                {h.label}
              </li>
            ))}
          </ul>

          {/* Countdown */}
          <div className="mt-10 w-full max-w-md animate-fade-up sm:mt-12 [animation-delay:0.35s]">
            <Countdown target={LAUNCH_TARGET} />
          </div>

          {/* Notify */}
          <div className="mt-9 flex w-full animate-fade-up flex-col items-center sm:mt-10 [animation-delay:0.45s]">
            <p className="mb-4 text-sm text-white/50">
              Be the first to know when we go live.
            </p>
            <NotifyForm />
          </div>

          {/* Mobile app badges */}
          <div className="mt-12 animate-fade-up [animation-delay:0.55s]">
            <AppBadges />
          </div>
        </section>

        {/* Footer */}
        <footer className="flex animate-fade-up flex-col items-center justify-between gap-4 border-t border-white/5 pt-6 text-xs text-white/40 sm:flex-row [animation-delay:0.5s]">
          <span>© {new Date().getFullYear()} Carpetta. All rights reserved.</span>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <a href="mailto:hello@carpetta.in" className="transition hover:text-ember-300">
              hello@carpetta.in
            </a>
            <a
              href="https://instagram.com/carpetta"
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-ember-300"
            >
              Instagram
            </a>
            <a
              href="https://linkedin.com/company/carpetta"
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-ember-300"
            >
              LinkedIn
            </a>
          </div>
        </footer>
      </div>
    </main>
  );
}
